import React from 'react';
import {
  QrCode, Store, Wallet, BarChart3,
  Shield, Smartphone, Globe, Headphones, Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const Services: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [servicesRef, servicesVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();
  const services = [
    {
      icon: <QrCode size={48} />,
      title: 'QR Payment Solutions',
      description: 'Accept and process payments instantly with secure QR codes. Support for both static and dynamic QR codes for flexible payment options.',
      features: ['Static & Dynamic QR Codes', 'Instant Payment Processing', 'Secure Transactions', 'Multi-currency Support'],
      link: '/services/qr-payment-solutions'
    },
    {
      icon: <Store size={48} />,
      title: 'Business Discovery Platform',
      description: 'Help customers discover your business with our comprehensive listing platform. Get found by users searching for services in your area.',
      features: ['Business Listings', 'Location-based Search', 'Category Management', 'Real-time Availability'],
      link: '/services/business-discovery-platform'
    },
    {
      icon: <Wallet size={48} />,
      title: 'Digital Wallet System',
      description: 'Complete wallet solution for both customers and businesses. Manage funds, track transactions, and process settlements seamlessly.',
      features: ['User & Business Wallets', 'Instant Transfers', 'Settlement Management', 'Transaction History'],
      link: '/services/digital-wallet-system'
    },
    {
      icon: <BarChart3 size={48} />,
      title: 'Analytics & Reporting',
      description: 'Comprehensive analytics dashboard to track your business performance, sales trends, and customer insights.',
      features: ['Sales Analytics', 'Customer Insights', 'Revenue Reports', 'Export to PDF/Excel'],
      link: '/services/analytics-reporting'
    },
    {
      icon: <Shield size={48} />,
      title: 'KYC & Verification',
      description: 'Secure business verification system ensuring all registered businesses are legitimate and trustworthy.',
      features: ['Document Verification', 'Business KYC', 'Real-time Status', 'Secure Storage'],
      link: '/services/kyc-verification'
    },
    {
      icon: <Smartphone size={48} />,
      title: 'Mobile Applications',
      description: 'Native mobile apps for iOS and Android, providing seamless experience for both customers and business owners.',
      features: ['iOS & Android Apps', 'Offline Capabilities', 'Push Notifications', 'Biometric Security'],
      link: '/services/mobile-applications'
    },
    {
      icon: <Globe size={48} />,
      title: 'Multi-platform Support',
      description: 'Access your business dashboard from anywhere with our responsive web platform and mobile apps.',
      features: ['Web Dashboard', 'Mobile Apps', 'API Integration', 'Third-party Integrations'],
      link: '/services/multi-platform-support'
    },
    {
      icon: <Headphones size={48} />,
      title: '24/7 Support',
      description: 'Round-the-clock customer support to help you with any questions or issues you may encounter.',
      features: ['Live Chat Support', 'Email Support', 'Phone Support', 'Help Center'],
      link: '/services/24-7-support'
    }
  ];

  return (
    <div className="min-h-screen bg-[#faf8ed]">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 md:pt-52 pb-20 md:pb-32 px-4 bg-mesh-premium relative overflow-hidden">
        {/* Animated Background Blobs */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10 overflow-hidden">
          <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-[#0c513f]/5 rounded-full blur-[120px] animate-blob"></div>
          <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-chowdeck-pastelPurple/20 rounded-full blur-[100px] animate-blob animation-delay-2000"></div>
        </div>

        <div className={`container-premium text-center transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
          }`}>
          <h1 className="heading-huge text-[#0c513f] mb-8 tracking-tighter-premium">
            Our Services
          </h1>
          <p className="text-2xl md:text-3xl text-secondary-900 max-w-3xl mx-auto leading-tight font-medium tracking-tight">
            Comprehensive solutions for modern businesses and customers.
            Everything you need for seamless digital payments.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section ref={servicesRef} className="py-20 px-4 bg-white rounded-chow-lg -mt-10 relative z-20">
        <div className="container-premium">
          <div className="grid md:grid-cols-2 gap-10">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className={`group bg-[#faf8ed] p-12 rounded-[3rem] transition-all duration-500 hover:shadow-2xl border-2 border-transparent hover:border-[#0c513f]/10 block ${servicesVisible
                  ? `animate-fade-in-up opacity-100`
                  : 'opacity-0'
                  }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="bg-white p-6 rounded-[2rem] text-[#0c513f] shadow-sm group-hover:scale-110 transition-transform duration-500">
                    {service.icon}
                  </div>
                  <div className="p-4 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-all duration-500 -translate-x-4 group-hover:translate-x-0">
                    <Zap className="text-[#0c513f]" size={24} fill="currentColor" />
                  </div>
                </div>

                <h3 className="text-4xl font-black text-[#0c513f] mb-6 tracking-tight">
                  {service.title}
                </h3>
                <p className="text-xl text-secondary-600 mb-8 font-medium leading-relaxed">
                  {service.description}
                </p>

                <div className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-secondary-800 font-bold bg-white/50 p-3 rounded-xl w-fit">
                      <div className="w-2 h-2 rounded-full bg-[#0c513f] mr-3"></div>
                      {feature}
                    </div>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Premium CTA Section */}
      <section ref={ctaRef} className="py-20 md:py-40 px-4 bg-[#0c513f] rounded-t-chow shadow-2xl mx-4 mb-10 overflow-hidden relative">
        <div className="absolute inset-0 bg-grid-premium opacity-10"></div>
        <div className={`max-w-5xl mx-auto text-center text-white relative z-10 transition-all duration-1000 ${ctaVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}>
          <h2 className="heading-huge mb-10 text-[#faf8ed] tracking-tightest-premium">
            Ready to <br /> get started?
          </h2>
          <p className="text-2xl md:text-3xl mb-16 text-[#faf8ed]/80 font-medium">
            Join thousands of businesses already using Rita <br /> for seamless payments and discovery.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
            <Link
              to="/contact"
              className="bg-[#faf8ed] text-[#0c513f] px-16 py-8 rounded-[2rem] font-black text-3xl hover:bg-white transition-all duration-300 shadow-2xl hover:scale-110 active:scale-95 min-w-[320px]"
            >
              Contact Sales
            </Link>
            <Link
              to="/app"
              className="border-4 border-[#faf8ed]/20 text-[#faf8ed] px-16 py-8 rounded-[2rem] font-black text-3xl hover:bg-[#faf8ed] hover:text-[#0c513f] transition-all duration-300 hover:scale-105 active:scale-95 min-w-[320px]"
            >
              Download App
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;

